import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import { IFormTypesGroupCategory } from "../../model/formconfiguration/FormTypesGroupCategory";
import Application from "../../Application";
import GetFormTypesGroupCategoriesInteractor from "../../interactor/formconfiguration/GetFormTypesGroupCategoriesInteractor";
import CreateFormTypesGroupCategoryInteractor from "../../interactor/formconfiguration/CreateFormTypesGroupCategoryInteractor";
import UpdateFormTypesGroupCategoryInteractor from "../../interactor/formconfiguration/UpdateFormTypesGroupCategoryInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import { IFormType } from "../../model/formconfiguration/FormType";
import GetFormTypesGroupsInteractor from "../../interactor/formconfiguration/GetFormTypesGroupsInteractor";
import FormTypesGroup, {
  IFormTypesGroup
} from "../../model/formconfiguration/FormTypesGroup";
import CreateFormTypesGroupsInteractor from "../../interactor/formconfiguration/CreateFormTypesGroupsInteractor";
import RemoveFormTypesGroupsInteractor from "../../interactor/formconfiguration/RemoveFormTypesGroupsInteractor";

export interface TFormTypesGroupCategoriesPresenter
  extends TLoadingAwarePresenter {
  formTypesGroupCategories: IFormTypesGroupCategory[];
  formTypes: IFormType[];
  expandedFormTypesGroupCategoryCode?: string;
  spinnerEnabled?: boolean;
  assignedFormTypesCodes?: string[];
  formTypesGroups?: IFormTypesGroup[];
}

export interface IFormTypesGroupCategoriesPresenter
  extends TFormTypesGroupCategoriesPresenter,
    TPresentable {
  setFormTypesGroupCategories(
    formTypesGroupCategories: IFormTypesGroupCategory[]
  ): void;
  createFormTypesGroupCategory(
    formTypesGroupCategory: IFormTypesGroupCategory
  ): void;
  updateFormTypesGroupCategory(
    formTypesGroupCategory: IFormTypesGroupCategory
  ): void;
  setFormTypes(formTypes: IFormType[]): void;
  onRowExpanded(expanded: any, record: any): void;
  onFormTypeSelectChange(selectedFormTypeCodes: string[]): void;
}

export interface IFormTypesPresentationModel extends IFormType {
  label: string;
  value: string;
}

const defaultState: TFormTypesGroupCategoriesPresenter = {
  formTypesGroupCategories: [],
  formTypes: []
};

const FormTypePresentationModelMapper = (
  formTypes: IFormType[]
): IFormTypesPresentationModel[] => {
  return formTypes.map((formType) => {
    return { ...formType, value: formType.code, label: formType.name };
  });
};

const AssignedFormTypesCodesMapper = (
  formTypesGroups: IFormTypesGroup[]
): string[] => {
  return formTypesGroups.map((formTypesGroup) => {
    return formTypesGroup.formTypeCode;
  });
};

const FormTypesGroupCategoriesPresenter = withStore<
  IFormTypesGroupCategoriesPresenter,
  TFormTypesGroupCategoriesPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IFormTypesGroupCategoriesPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TFormTypesGroupCategoriesPresenter>();

    loader.start("formTypesGroupLoader");

    const setFormTypesGroupCategories = (
      formTypesGroupCategories: IFormTypesGroupCategory[]
    ) => {
      _store.update({ formTypesGroupCategories });
      loader.stop("formTypesGroupLoader");
    };

    const getFormTypesGroupCategories = async () => {
      try {
        let formTypesGroupCategories = await _application.container
          .resolve<GetFormTypesGroupCategoriesInteractor>(
            "getFormTypesGroupCategories"
          )
          .execute();
        _store.update({ formTypesGroupCategories });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const createFormTypesGroupCategory = async (
      formTypesGroupCategory: IFormTypesGroupCategory
    ) => {
      try {
        await _application.container
          .resolve<CreateFormTypesGroupCategoryInteractor>(
            "createFormTypesGroupCategory"
          )
          .execute(formTypesGroupCategory);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate(
              "form_types_group_category_form.messages.create_formTypesGroupCategory_success"
            )
          );
        await getFormTypesGroupCategories();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const updateFormTypesGroupCategory = async (
      formTypesGroupCategory: IFormTypesGroupCategory
    ) => {
      try {
        await _application.container
          .resolve<UpdateFormTypesGroupCategoryInteractor>(
            "updateFormTypesGroupCategory"
          )
          .execute(formTypesGroupCategory);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate(
              "form_types_group_category_form.messages.update_formTypesGroupCategory_success"
            )
          );
        await getFormTypesGroupCategories();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const setFormTypes = (formTypes: IFormType[]) => {
      formTypes = FormTypePresentationModelMapper(formTypes);
      _store.update({ formTypes });
    };

    const enableSpinner = () => {
      _store.update({ spinnerEnabled: true });
    };

    const disableSpinner = () => {
      _store.update({ spinnerEnabled: false });
    };

    const getFormTypesGroups = async (roleTypesGroupCategoryCode: string) => {
      try {
        let formTypesGroups = await _application.container
          .resolve<GetFormTypesGroupsInteractor>("getFormTypesGroups")
          .execute(roleTypesGroupCategoryCode);
        let assignedFormTypesCodes =
          AssignedFormTypesCodesMapper(formTypesGroups);
        _store.update({ formTypesGroups, assignedFormTypesCodes });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const setExpandedRowData = async (formTypesGroupCategoryCode: string) => {
      enableSpinner();
      await getFormTypesGroups(formTypesGroupCategoryCode);
      disableSpinner();
    };

    const setExpandedFormTypesGroupCategoryCode = (
      expandedFormTypesGroupCategoryCode: string
    ) => {
      _store.update({ expandedFormTypesGroupCategoryCode });
    };

    const onRowExpanded = async (expanded: any, record: any) => {
      if (expanded) {
        setExpandedFormTypesGroupCategoryCode(record.code);
        await setExpandedRowData(record.code);
      }
    };

    const setAssignedFormTypesCodes = (assignedFormTypesCodes: string[]) => {
      _store.update({ assignedFormTypesCodes });
      disableSpinner();
    };

    const getFormTypesGroupsFromFormTypesCodes = (formTypesCodes: string[]) => {
      let { expandedFormTypesGroupCategoryCode } = _store.getState();
      return formTypesCodes.map((formTypeCode) => {
        return FormTypesGroup({
          id: 0,
          formTypesGroupCategoryCode: expandedFormTypesGroupCategoryCode,
          formTypeCode: formTypeCode
        });
      });
    };

    const getFormTypesGroupsIdsFromFormTypesCodes = (
      formTypesCodes: string[]
    ) => {
      let { formTypesGroups } = _store.getState();

      let formTypesGroupsIds: number[] = [];

      formTypesGroups.forEach((formTypesGroup) => {
        formTypesCodes.forEach((formTypeCode) => {
          if (formTypesGroup.formTypeCode === formTypeCode) {
            formTypesGroupsIds.push(formTypesGroup.id);
          }
        });
      });
      return formTypesGroupsIds;
    };

    const createFormTypesGroups = async (formTypesCodes: string[]) => {
      let { expandedFormTypesGroupCategoryCode } = _store.getState();
      try {
        let formTypesGroups =
          getFormTypesGroupsFromFormTypesCodes(formTypesCodes);
        await _application.container
          .resolve<CreateFormTypesGroupsInteractor>("createFormTypesGroups")
          .execute(formTypesGroups);
        await getFormTypesGroups(expandedFormTypesGroupCategoryCode);
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const removeFormTypesGroups = async (formTypesCodes: string[]) => {
      let { expandedFormTypesGroupCategoryCode } = _store.getState();
      try {
        await getFormTypesGroupCategories();
        let formTypesGroupsIds =
          getFormTypesGroupsIdsFromFormTypesCodes(formTypesCodes);

        await _application.container
          .resolve<RemoveFormTypesGroupsInteractor>("removeFormTypesGroups")
          .execute(formTypesGroupsIds);
        await getFormTypesGroups(expandedFormTypesGroupCategoryCode);
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const onFormTypeSelectChange = async (selectedFormTypesCodes: string[]) => {
      let { assignedFormTypesCodes } = _store.getState();
      enableSpinner();
      if (selectedFormTypesCodes.length > assignedFormTypesCodes.length) {
        let newFormTypesCodes = selectedFormTypesCodes.filter(
          (selectedFormTypesCode) =>
            !assignedFormTypesCodes.includes(selectedFormTypesCode)
        );

        await createFormTypesGroups(newFormTypesCodes);
      } else if (
        selectedFormTypesCodes.length < assignedFormTypesCodes.length
      ) {
        let removedFormTypesCodes = assignedFormTypesCodes.filter(
          (assignedRoleTypeCode) =>
            !selectedFormTypesCodes.includes(assignedRoleTypeCode)
        );
        await removeFormTypesGroups(removedFormTypesCodes);
      }
      setAssignedFormTypesCodes(selectedFormTypesCodes);
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      setFormTypesGroupCategories,
      createFormTypesGroupCategory,
      updateFormTypesGroupCategory,
      setFormTypes,
      onRowExpanded,
      onFormTypeSelectChange
    };
  },
  defaultState
);

export default FormTypesGroupCategoriesPresenter;
